import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
} from "@mui/material";
import {
  useAssignExerciseDayToClassMutation,
  useUnassignExerciseDayFromClassMutation,
  useGetExerciseDayAssignedClassesQuery,
} from "@/store/api/admin/exerciseApi";
import DeleteIcon from "@mui/icons-material/Delete";
import SchoolIcon from "@mui/icons-material/School";
import GroupIcon from "@mui/icons-material/Group";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AssignmentIcon from "@mui/icons-material/Assignment";
import { ConfirmDialog } from "./ConfirmDialog";
import ClassMultiSelect from "./ClassMultiSelect";

const ExerciseAssignmentDialog = React.memo(
  ({ open, onClose, exercise }) => {
    const [selectedDay, setSelectedDay] = useState(null);
    const [selectedClasses, setSelectedClasses] = useState([]);
    const [assignmentError, setAssignmentError] = useState("");
    const [assignmentSuccess, setAssignmentSuccess] = useState("");
    const [expandedDay, setExpandedDay] = useState(null);
    const [dialogDelete, setDialogDelete] = useState({
      open: false,
      dayId: null,
      classId: null,
      title: "",
      message: "",
      confirmText: "",
      cancelText: "",
    });

    const {
      data: assignedClassesData,
      isLoading: assignedClassesLoading,
      refetch: refetchAssignedClasses,
    } = useGetExerciseDayAssignedClassesQuery(
      { exerciseId: exercise?._id, dayId: selectedDay?._id },
      {
        skip: !exercise?._id || !selectedDay?._id || !open,
      }
    );

    const [assignExerciseDayToClass, { isLoading: isAssigning }] =
      useAssignExerciseDayToClassMutation();
    const [unassignExerciseDayFromClass, { isLoading: isRemoving }] =
      useUnassignExerciseDayFromClassMutation();

    useEffect(() => {
      if (open) {
        setSelectedDay(null);
        setSelectedClasses([]);
        setAssignmentError("");
        setAssignmentSuccess("");
        setExpandedDay(null);
      }
    }, [open]);

    useEffect(() => {
      if (selectedDay) {
        setSelectedClasses([]);
        setAssignmentError("");
        setAssignmentSuccess("");
      }
    }, [selectedDay]);

    const handleAssignClasses = useCallback(async () => {
      if (!selectedDay) {
        setAssignmentError("Please select a day first.");
        return;
      }

      if (selectedClasses.length === 0) {
        setAssignmentError("Please select at least one class to assign.");
        return;
      }

      try {
        // Assign each class individually
        const promises = selectedClasses.map((classItem) =>
          assignExerciseDayToClass({
            exerciseId: exercise._id,
            dayId: selectedDay._id,
            classId: classItem._id,
          }).unwrap()
        );

        await Promise.all(promises);

        setAssignmentSuccess(
          `Successfully assigned day "${selectedDay.title}" to ${selectedClasses.length} class(es).`
        );
        setSelectedClasses([]);
        refetchAssignedClasses();
        setAssignmentError("");
      } catch (error) {
        console.error("Error assigning exercise day:", error);
        setAssignmentError(
          error?.data?.message || "Failed to assign exercise day to classes."
        );
        setAssignmentSuccess("");
      }
    }, [
      selectedClasses,
      selectedDay,
      exercise?._id,
      assignExerciseDayToClass,
      refetchAssignedClasses,
    ]);

    const confirmToDeleteAssignment = useCallback((dayId, classId) => {
      setDialogDelete({
        open: true,
        dayId,
        classId,
        title: "Confirm to remove assignment",
        onClose: () => setDialogDelete({ ...dialogDelete, open: false }),
        message: `Are you sure you want to remove this exercise day assignment from the class?`,
        confirmText: "Yes",
        cancelText: "No",
      });
    }, [dialogDelete]);

    const handleRemoveAssignment = useCallback(
      async () => {
        try {
          await unassignExerciseDayFromClass({
            exerciseId: exercise._id,
            dayId: dialogDelete.dayId,
            classId: dialogDelete.classId,
          }).unwrap();

          refetchAssignedClasses();
          setAssignmentSuccess("Class assignment removed successfully.");
          setAssignmentError("");
          setDialogDelete({ ...dialogDelete, open: false });
        } catch (error) {
          console.error("Error removing assignment:", error);
          setAssignmentError("Failed to remove class assignment.");
          setAssignmentSuccess("");
        }
      },
      [exercise?._id, dialogDelete, unassignExerciseDayFromClass, refetchAssignedClasses]
    );

    const assignedClasses = useMemo(
      () => assignedClassesData?.data || [],
      [assignedClassesData?.data]
    );

    const assignedClassIds = useMemo(
      () => assignedClasses.map((assignment) => assignment.class?._id),
      [assignedClasses]
    );

    const handleAccordionChange = useCallback((dayId) => (event, isExpanded) => {
      setExpandedDay(isExpanded ? dayId : null);
    }, []);

    if (!exercise) return null;

    const days = exercise.days || [];

    return (
      <>
        <Dialog
          open={open}
          onClose={onClose}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: "0 24px 48px rgba(0,0,0,0.15)",
            },
          }}
        >
          <DialogTitle
            sx={{
              background: "linear-gradient(135deg, #FF9800 0%, #F57C00 100%)",
              color: "white",
              py: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AssignmentIcon />
              <Typography variant="h6">
                Assign Exercise Days: {exercise.title}
              </Typography>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ p: 3 }}>
            {(assignmentError || assignmentSuccess) && (
              <Box sx={{ mb: 2 }}>
                {assignmentError && (
                  <Alert severity="error" sx={{ mb: 1 }}>
                    {assignmentError}
                  </Alert>
                )}
                {assignmentSuccess && (
                  <Alert severity="success" sx={{ mb: 1 }}>
                    {assignmentSuccess}
                  </Alert>
                )}
              </Box>
            )}

            {days.length === 0 ? (
              <Alert severity="info">
                This exercise has no days configured yet.
              </Alert>
            ) : (
              <Grid container spacing={3}>
                {/* Days Selection Section */}
                <Grid item xs={12} md={4}>
                  <Typography
                    variant="h6"
                    sx={{ mb: 2, color: "#FF9800", fontWeight: 600 }}
                  >
                    <CalendarTodayIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                    Select Day
                  </Typography>

                  <Box sx={{ maxHeight: 400, overflow: "auto" }}>
                    {days.map((day) => (
                      <Card
                        key={day._id}
                        onClick={() => setSelectedDay(day)}
                        sx={{
                          mb: 1,
                          cursor: "pointer",
                          border: selectedDay?._id === day._id ? "2px solid #FF9800" : "1px solid rgba(0,0,0,0.12)",
                          bgcolor: selectedDay?._id === day._id ? "rgba(255, 152, 0, 0.08)" : "white",
                          transition: "all 0.2s",
                          "&:hover": {
                            boxShadow: 2,
                            transform: "translateY(-2px)",
                          },
                        }}
                      >
                        <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}>
                          <Typography variant="subtitle1" fontWeight={600}>
                            Day {day.dayNumber}: {day.title}
                          </Typography>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                </Grid>

                {/* Assign Classes Section */}
                <Grid item xs={12} md={4}>
                  <Typography
                    variant="h6"
                    sx={{ mb: 2, color: "#FF9800", fontWeight: 600 }}
                  >
                    <SchoolIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                    Assign to Classes
                  </Typography>

                  {!selectedDay ? (
                    <Alert severity="info">
                      Please select a day first to assign classes.
                    </Alert>
                  ) : (
                    <>
                      <ClassMultiSelect
                        value={selectedClasses}
                        onChange={setSelectedClasses}
                        excludeIds={assignedClassIds}
                        emptyMessage="All classes are already assigned to this day"
                        chipColor="#fff3e0"
                      />

                      <Button
                        variant="contained"
                        onClick={handleAssignClasses}
                        disabled={isAssigning || selectedClasses.length === 0}
                        startIcon={
                          isAssigning ? (
                            <CircularProgress size={20} />
                          ) : (
                            <SchoolIcon />
                          )
                        }
                        fullWidth
                        sx={{
                          background:
                            "linear-gradient(135deg, #4CAF50 0%, #45a049 100%)",
                          py: 1.5,
                          borderRadius: 2,
                        }}
                      >
                        {isAssigning
                          ? "Assigning..."
                          : `Assign to ${selectedClasses.length} Class(es)`}
                      </Button>
                    </>
                  )}
                </Grid>

                {/* Currently Assigned Classes Section */}
                <Grid item xs={12} md={4}>
                  <Typography
                    variant="h6"
                    sx={{ mb: 2, color: "#FF9800", fontWeight: 600 }}
                  >
                    <GroupIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                    Assigned Classes
                  </Typography>

                  {!selectedDay ? (
                    <Alert severity="info">
                      Select a day to view assigned classes.
                    </Alert>
                  ) : assignedClassesLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : assignedClasses.length === 0 ? (
                    <Alert severity="info">
                      No classes assigned to this day yet
                    </Alert>
                  ) : (
                    <List
                      sx={{
                        bgcolor: "background.paper",
                        borderRadius: 2,
                        border: "1px solid rgba(0,0,0,0.12)",
                        maxHeight: 300,
                        overflow: "auto",
                      }}
                    >
                      {assignedClasses.map((assignment, index) => (
                        <React.Fragment key={assignment._id}>
                          {index > 0 && <Divider />}
                          <ListItem>
                            <ListItemText
                              primary={assignment?.class?.classCode}
                              secondary={
                                assignment?.class?.className ||
                                `${assignment?.class?.studentCount || 0} students`
                              }
                              primaryTypographyProps={{ fontWeight: 500 }}
                            />
                            <ListItemSecondaryAction>
                              <IconButton
                                edge="end"
                                onClick={() =>
                                  confirmToDeleteAssignment(
                                    selectedDay._id,
                                    assignment.class._id
                                  )
                                }
                                disabled={isRemoving}
                                sx={{
                                  color: "#f44336",
                                  "&:hover": {
                                    bgcolor: "rgba(244, 67, 54, 0.08)",
                                  },
                                }}
                              >
                                {isRemoving ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <DeleteIcon />
                                )}
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </Grid>
              </Grid>
            )}
          </DialogContent>

          <DialogActions sx={{ p: 3, pt: 1 }}>
            <Button
              onClick={onClose}
              sx={{
                color: "#666",
                "&:hover": {
                  bgcolor: "rgba(0,0,0,0.04)",
                },
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
        <ConfirmDialog
          open={dialogDelete.open}
          onClose={dialogDelete.onClose}
          title={dialogDelete.title}
          message={dialogDelete.message}
          onConfirm={handleRemoveAssignment}
          confirmText={dialogDelete.confirmText}
          cancelText={dialogDelete.cancelText}
        />
      </>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison function to prevent unnecessary re-renders
    return (
      prevProps.open === nextProps.open &&
      prevProps.onClose === nextProps.onClose &&
      prevProps.exercise?._id === nextProps.exercise?._id
    );
  }
);

ExerciseAssignmentDialog.displayName = "ExerciseAssignmentDialog";

export default ExerciseAssignmentDialog;
